import { Box, Grid, Stack } from '@mui/material'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import InfoText from '@/components/dataDisplay/InfoText'
import SwitchController from '@/components/inputs/SwitchController'
import { useAlertContext } from '@/contexts/AlertContext'
import CancelConfirmIconButtons from '@/features/site/components/CancelConfirmIconButtons'
import { SITE_STEPS_FIELD_WIDTH } from '@/features/site/constants'
import { useSiteActivationOrderSettingsMutation } from '@/features/site/hooks/useSiteActivationOrderSettingsMutation'
import { useInvalidateSiteQuery } from '@/features/site/hooks/useSiteQuery'
import type { Site, SiteActivationOrderSettingsRequest } from '@/features/site/types/site'
import { errorHandler } from '@/utils/errorHandler'

interface SiteActivationOrderSettingsFormProps {
  isTopPlacementPriority: boolean
  site: Site
  onReset: () => void
  onSubmit: () => void
}

export const SiteActivationOrderSettingsForm = ({
  isTopPlacementPriority,
  site,
  onReset,
  onSubmit,
}: SiteActivationOrderSettingsFormProps) => {
  const { t } = useTranslation()
  const form = useForm<SiteActivationOrderSettingsRequest>({
    mode: 'onBlur',
    defaultValues: {
      isTopPlacementPriority: isTopPlacementPriority,
    },
  })

  const { updateSiteActivationOrderSettings, isPending } = useSiteActivationOrderSettingsMutation()
  const invalidateSiteQuery = useInvalidateSiteQuery()
  const { pushAlert } = useAlertContext()

  useEffect(() => {
    if (isTopPlacementPriority !== null) {
      form.setValue('isTopPlacementPriority', isTopPlacementPriority)
    }
  }, [isTopPlacementPriority])

  async function handleSubmit(formData: SiteActivationOrderSettingsRequest) {
    try {
      await updateSiteActivationOrderSettings({
        siteUuid: site.uuid,
        location: site.location!,
        payload: {
          isTopPlacementPriority: formData.isTopPlacementPriority,
        },
      })
      await invalidateSiteQuery({ uuid: site.uuid })

      pushAlert({
        message: t('sites.activation_order_settings.form.save_success'),
        severity: 'success',
      })

      onSubmit()
    } catch (err) {
      const error = errorHandler(err, t('sites.activation_order_settings.form.save_error'))

      pushAlert({
        severity: 'error',
        message: error.message,
      })
    }
  }

  function handleReset() {
    onReset()

    form.reset({
      isTopPlacementPriority: site.isTopPlacementPriority,
    })
  }

  return (
    <FormProvider {...form}>
      <Stack
        noValidate
        component="form"
        direction="row"
        onReset={handleReset}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <Box flexGrow={1}>
          <Grid container direction="column" gap={2}>
            <Grid size={{ md: 8, sm: 6 }}>
              <InfoText text={t('sites.activation_order_settings.form.info')} />
            </Grid>

            <Grid width={SITE_STEPS_FIELD_WIDTH}>
              <Stack flexDirection="column" gap={3}>
                <SwitchController
                  label={t('sites.activation_order_settings.form.is_top_placement_priority.label')}
                  name="isTopPlacementPriority"
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <CancelConfirmIconButtons disabled={isPending} />
      </Stack>
    </FormProvider>
  )
}
